import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { mdiHelpCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";

import Button from "components/Button";
import Input from "components/Input";
import Select from "components/Select";
import { LightBulbSVGIcon } from "components/SVGIcons";
import { routes } from "constants/routes";
import HistoryPaymentItem from "features/main/HaulPay/modules/Carrier/HistoryPaymentItem";
import { QUERY_CARRIER_FUNDING_REQUEST } from "features/main/HaulPay/Shared/EditFundingRequest/Manage/query";
import { formatNumber } from "helpers/formatNumber";
import { IFRAttachment, InformationFormData } from "models/HaulPay";
import { calculateFRAPI } from "services/api/haulpay";
import styles from "./style.module.scss";

interface IProps {
  informationFormData?: InformationFormData;
  handleNext: VoidFunction;
  createdFR: any;
}

export default function PayConfirmation({
  informationFormData,
  handleNext,
  createdFR
}: Readonly<IProps>) {
  const navigate = useNavigate();
  const [totalToBePaid, setTotalToBePaid] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<IFRAttachment[]>([]);

  const { data } = useQuery(QUERY_CARRIER_FUNDING_REQUEST, {
    variables: {
      id: createdFR.id
    },
    fetchPolicy: "cache-and-network"
  });

  const goToFrDetails = () => {
    navigate(`${routes.HAULPAY_CARRIER_FUNDING_REQUEST}/${createdFR.id}`);
  };

  useEffect(() => {
    if (informationFormData) {
      calculateFRAPI({
        debtor: informationFormData.billToCompany?.id ?? "",
        factored: informationFormData.isNonFactoredInvoice !== null,
        invoice_amount:
          informationFormData.totalInvoiceAmount * 100 -
          (informationFormData.advanceAmount ?? 0) * 100,
        create_rates_revision: false,
        payment_profile_amount: 0
      }).then((response) => {
        setTotalToBePaid(response.data.factoring_payable / 100);
      });
    }
  }, [informationFormData]);

  useEffect(() => {
    if (data?.fundingRequest) {
      setUploadedFiles(data.fundingRequest.attachments as IFRAttachment[]);
    }
  }, [data]);

  return (
    <div className={styles.payConfirmationContainer}>
      <div className={styles.body}>
        <h4 className={styles.header}>Confirmation</h4>

        <div className={styles.paymentTime}>
          <span className={styles.description}>Cutoff time is 12PM CT for next day payment.</span>
          <div className={styles.paymentTimeRight}>
            <button>
              <Icon path={mdiHelpCircleOutline} className="size-5 text-primary" />
            </button>
            <button>
              <LightBulbSVGIcon className="text-primary" />
            </button>
          </div>
        </div>

        <div className={styles.inputField}>
          <Input
            label="&nbsp;&nbsp;Total Invoice Amount"
            defaultValue={
              informationFormData && formatNumber(informationFormData.totalInvoiceAmount, 2)
            }
            disabled
          />
          <Input
            label="&nbsp;&nbsp;Load Number"
            defaultValue={informationFormData?.loadNumber}
            disabled
          />
          <Input
            label="&nbsp;&nbsp;Bill To Company"
            defaultValue={informationFormData?.billToCompany?.dba}
            disabled
          />
          <Input
            label="&nbsp;&nbsp;Is this a non-factored invoice?"
            defaultValue={informationFormData?.isNonFactoredInvoice ? "Yes" : "No"}
            disabled
          />
          <Input
            label="&nbsp;&nbsp;Any fuel advance?"
            defaultValue={informationFormData?.isFuelAdvance ? "Yes" : "No"}
            disabled
          />
          <Select
            label="&nbsp;&nbsp;Payment Method"
            options={informationFormData?.paymentMethod ? [informationFormData?.paymentMethod] : []}
            value={informationFormData?.paymentMethod}
            disabled
          />
        </div>

        {uploadedFiles.length ? (
          <div className={styles.uploadFileList}>
            {uploadedFiles.map((item) => (
              <HistoryPaymentItem
                key={item.id}
                label={item.filename}
                category={
                  typeof item.category === "string" ? item.category : item.category.join(",")
                }
                data={{ preview_url: item.downloadUrl } as any}
              />
            ))}
          </div>
        ) : null}

        <div className={styles.totalPayment}>
          <span className={styles.rate}>Your Rate: {informationFormData?.discountRate ?? 0}%</span>
          <div className="flex justify-between">
            <div className="text-text">
              <span className="font-medium desktop:text-xl">Total </span>
              <span className="text-sm desktop:text-base">(To be Paid)</span>
            </div>
            <span className={styles.totalAmount}>${totalToBePaid.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="outline" onClick={goToFrDetails}>
          Edit
        </Button>
        <Button onClick={handleNext}>Confirm</Button>
      </div>
    </div>
  );
}
